import { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { useAppSelector, useAppDispatch } from '../../hooks'

import InputNumberDiwa from '../Generic/InputNumberDiwa'
import { removeDiscountToSave, setDiscountToSave } from '../../Slices/Omnichannel/DiscountSlice'
import { key } from 'Helpers/DiscountHelper'
import { acquireLock } from '../../Services/Omnichannel/LockService'

type Props = {
  editDisabled: boolean
  superGroup?: number
  sector?: number
  channel?: string
  brand?: string
  sectorDiscounts?: Map<string, any>
  priceListDiscounts?: Map<string, any>
  brandDiscounts?: Map<string, any>
  arrowNavigation?: boolean
}

function DiscountInput(props: Props) {
  const dispatch = useAppDispatch()
  const discountData = useAppSelector(state => state.present.discount.discountData)

  const listings = useAppSelector(state => state.present.configuration.allPricelists)
  const discountToSave = useAppSelector(state => state.present.discount.discountToSave)
  const isdisable = props.editDisabled
  const listingInformation = props.channel ? listings.find(x => x.name === props.channel) : false
  const isListingTypeSell = listingInformation ? listingInformation.isSell : true

  const [inputActive, setInputActive] = useState(!isdisable)

  const [isUnique, isValueChanged, originalDiscountValue, discountValue] = useMemo(() => {
    const getBrandDiscount = () => {
      const brandDiscount = props.brandDiscounts?.get(key(props.superGroup, props.sector, props.channel, props.brand))
      if (!brandDiscount) {
        return [null, null]
      }

      const brandChanged = discountToSave.find(
        x =>
          x.superGroup === props.superGroup &&
          x.sector === props.sector &&
          x.channel === props.channel &&
          x.brand === props.brand,
      )

      const discount =
        brandChanged && (brandChanged.discount === 0 || brandChanged.discount)
          ? brandChanged.discount
          : brandChanged?.cleared
          ? null
          : brandDiscount?.discount

      return [discount, brandDiscount?.discount]
    }

    const getPricelistDiscount = () => {
      const pricelistDiscount = props.priceListDiscounts?.get(key(props.superGroup, props.sector, props.channel))
      if (!pricelistDiscount) {
        return [null, null]
      }

      const pricelistChanged = discountToSave.find(
        x => x.superGroup === props.superGroup && x.sector === props.sector && x.channel === props.channel && !x.brand,
      )

      const discount =
        pricelistChanged && (pricelistChanged.discount === 0 || pricelistChanged.discount)
          ? pricelistChanged.discount
          : pricelistChanged?.cleared
          ? null
          : pricelistDiscount?.discount
      return [discount, pricelistDiscount?.discount]
    }

    const getSectorDiscount = () => {
      const sectorDiscount = props.sectorDiscounts?.get(key(props.sector, props.superGroup))
      if (!sectorDiscount) {
        return [null, null]
      }

      const sectorChanged = discountToSave.find(
        x => x.superGroup === props.superGroup && x.sector === props.sector && !x.channel && !x.brand,
      )

      const discount =
        sectorChanged && (sectorChanged.discount === 0 || sectorChanged.discount)
          ? sectorChanged.discount
          : sectorChanged?.cleared
          ? null
          : sectorDiscount?.discount

      return [discount, sectorDiscount?.discount]
    }

    const getGeneralDiscount = () => {
      const generalValue = discountData?.generalDiscount ?? 0
      const generalChanged = discountToSave.find(x => !x.superGroup && !x.sector && !x.channel && !x.brand)

      const discount =
        generalChanged && (generalChanged.discount === 0 || generalChanged.discount)
          ? generalChanged.discount
          : generalValue

      return [discount, generalValue]
    }

    const calculateReturnValues = (discount, originalDiscount, refDiscount, refOriginalDiscount) => {
      return [
        (discount === 0 || discount) && discount !== refDiscount,
        ((discount === 0 || discount) && discount !== originalDiscount) ||
          (!discount && discount !== 0 && (refDiscount === 0 || refDiscount) && refDiscount !== refOriginalDiscount),
        originalDiscount === 0 || originalDiscount ? originalDiscount : refOriginalDiscount,
        discount === 0 || discount ? discount : refDiscount,
      ]
    }

    if (props.brand) {
      const [discount, originalDiscount] = getBrandDiscount()
      let [refDiscount, refOriginalDiscount] = getPricelistDiscount()
      if ((!refDiscount && refDiscount !== 0) || (!refOriginalDiscount && refOriginalDiscount !== 0)) {
        const [sectorDiscount, originalSectorDiscount] = getSectorDiscount()
        if (!refDiscount && refDiscount !== 0) {
          refDiscount = sectorDiscount
        }
        if (!refOriginalDiscount && refOriginalDiscount !== 0) {
          refOriginalDiscount = originalSectorDiscount
        }
      }
      if ((!refDiscount && refDiscount !== 0) || (!refOriginalDiscount && refOriginalDiscount !== 0)) {
        const [generalDiscount, originalGeneralDiscount] = getGeneralDiscount()
        if (!refDiscount && refDiscount !== 0) {
          refDiscount = generalDiscount
        }
        if (!refOriginalDiscount && refOriginalDiscount !== 0) {
          refOriginalDiscount = originalGeneralDiscount
        }
      }
      return calculateReturnValues(discount, originalDiscount, refDiscount, refOriginalDiscount)
    } else if (props.channel) {
      const [discount, originalDiscount] = getPricelistDiscount()
      let [refDiscount, refOriginalDiscount] = getSectorDiscount()
      if ((!refDiscount && refDiscount !== 0) || (!refOriginalDiscount && refOriginalDiscount !== 0)) {
        const [generalDiscount, originalGeneralDiscount] = getGeneralDiscount()
        if (!refDiscount && refDiscount !== 0) {
          refDiscount = generalDiscount
        }
        if (!refOriginalDiscount && refOriginalDiscount !== 0) {
          refOriginalDiscount = originalGeneralDiscount
        }
      }
      return calculateReturnValues(discount, originalDiscount, refDiscount, refOriginalDiscount)
    } else if (props.sector) {
      const [discount, originalDiscount] = getSectorDiscount()
      const [refDiscount, refOriginalDiscount] = getGeneralDiscount()
      return calculateReturnValues(discount, originalDiscount, refDiscount, refOriginalDiscount)
    } else {
      const [generalDiscount, originalGeneralDiscount] = getGeneralDiscount()
      return [false, generalDiscount !== originalGeneralDiscount, originalGeneralDiscount, generalDiscount]
    }
  }, [
    discountData?.generalDiscount,
    discountToSave,
    props.brand,
    props.brandDiscounts,
    props.channel,
    props.priceListDiscounts,
    props.sector,
    props.sectorDiscounts,
    props.superGroup,
  ])

  useEffect(() => {
    setInputActive(!isdisable)
  }, [isdisable])

  const storeDiscount = useCallback(
    value => {
      const discountRounded = value || value === 0 ? Math.round(value) : value
      if (originalDiscountValue === discountRounded) {
        dispatch(
          removeDiscountToSave({
            superGroup: props.superGroup,
            sector: props.sector,
            channel: props.channel,
            brand: props.brand,
          }),
        )
        setInputActive(!isdisable)
        return new Promise(resolve => {
          resolve(true)
        })
      } else {
        setInputActive(!isdisable)
        return dispatch(acquireLock(false)).then(x => {
          if (x) {
            dispatch(
              setDiscountToSave({
                superGroup: props.superGroup,
                sector: props.sector,
                channel: props.channel,
                brand: props.brand,
                discount: discountRounded,
              }),
            )
            return x
          }
        })
      }
    },
    [isdisable, originalDiscountValue, dispatch, props.superGroup, props.sector, props.channel, props.brand],
  )

  const setEditIput = useCallback(() => {
    setInputActive(!isdisable)
  }, [isdisable])

  const onInputPress = event => {
    if (event.key === 'Escape') {
      setInputActive(false)
    }
  }

  const className = useMemo(() => {
    return (
      (discountValue === 0 || discountValue
        ? (!inputActive ? ' count-box ' : ' num-input discount-input') +
          (isUnique ? ' bold' : '') +
          (isValueChanged ? ' changed-box' : '') +
          (isListingTypeSell ? '' : ' sale')
        : ' count-box empty-content') + (props.brand ? ' brand-count-box' : '')
    )
  }, [discountValue, inputActive, isListingTypeSell, isUnique, isValueChanged, props.brand])

  return (
    <InputNumberDiwa
      min={0}
      max={isListingTypeSell ? 100 : null}
      className={`${className}${props.arrowNavigation ? ' arrow-navigation' : ''}`}
      controls={false}
      value={discountValue}
      onValueConfirm={inputActive ? storeDiscount : undefined}
      onKeyDown={inputActive ? onInputPress : undefined}
      onFocus={setEditIput}
      readOnly={isdisable}
      focusSelectAll={!isdisable}
    />
  )
}

export default memo(DiscountInput)
